import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import CodeHighlight from './CodeHighlight';

/**
 * Component to display data elements in a consistent manner
 */
const DataPanel = ({ 
  title, 
  subtitle = null, 
  data = null, 
  tag = null, 
  bordered = true, 
  elevation = 0,
  className = '',
  children = null
}) => {
  return (
    <Paper 
      elevation={elevation}
      sx={{
        mb: 2,
        overflow: 'hidden',
        border: bordered ? 1 : 'none',
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: 'primary.light',
        borderRadius: 3,
        background: theme => theme.palette.mode === 'light' 
          ? 'linear-gradient(180deg, rgba(99,102,241,0.06) 0%, rgba(99,102,241,0.03) 100%)'
          : 'linear-gradient(180deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.06) 100%)'
      }}
      className={className}
    >
      <Box 
        sx={{ 
          p: 2, 
          background: theme => theme.palette.mode === 'light' 
            ? 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(245,247,255,0.9) 100%)'
            : 'linear-gradient(180deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.9) 100%)',
          borderBottom: data || children ? 1 : 'none',
          borderBottomColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography 
            variant="subtitle1" 
            component="h3"
            sx={{ 
              fontWeight: 600,
              letterSpacing: 0.2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {title}
            {subtitle && (
              <Typography 
                component="span" 
                variant="body2" 
                sx={{ 
                  ml: 1, 
                  color: 'text.secondary',
                  fontWeight: 400
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Typography>
        </Box>
        
        {tag && (
          <Chip 
            label={tag} 
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              boxShadow: '0 1px 6px rgba(63,81,181,0.25)'
            }}
          />
        )}
      </Box>
      
      {(data || children) && (
        <Box sx={{ p: 2 }}>
          {data && (
            <Box>
              {data.map((item, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: index < data.length - 1 ? 2 : 0,
                    '&:not(:last-child)': {
                      pb: 2,
                      borderBottom: theme => theme.palette.mode === 'light' 
                        ? '1px dashed rgba(0, 0, 0, 0.12)'
                        : '1px dashed rgba(255, 255, 255, 0.12)'
                    }
                  }}
                >
                  {item.label && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                  
                  <Box sx={{ pl: item.label ? 1 : 0 }}>
                    {typeof item.value === 'string' ? (
                      <Typography variant="body1" component="div">
                        {item.code ? (
                          <CodeHighlight 
                            code={item.code} 
                            description={item.value} 
                            tooltip={item.tooltip}
                            type={item.type || 'default'} 
                          />
                        ) : (
                          item.value
                        )}
                      </Typography>
                    ) : (
                      item.value
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          
          {children}
        </Box>
      )}
    </Paper>
  );
};

export default DataPanel; 